import { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { useFormikContext } from 'formik';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { toastr } from 'react-redux-toastr';

import FormModal from '../../core/containers/FormModal';
import StringInput from '../../core/components/forms/StringInput';
import SelectInput from '../../core/components/forms/SelectInput';
import { Button } from '../../core/components/buttons';
import APPConf from '../../../lib/config/common';
import * as URL from '../../../lib/router/url';
import { feedbackActions } from '../store/feedbackStore';
import { ConsoleFeedbackValidationSchema } from '../validation';

// Счётчик символов под полем (live из Formik). Краснеет при превышении лимита.
const CharCounter = (props) => {
  const { name, max } = props;
  const { values } = useFormikContext();
  const length = (values?.[name] ?? '').length;
  return (
    <small
      className={ length > max ? 'text-danger' : 'text-muted' }
      style={ { display: 'block', textAlign: 'right', marginTop: 2 } }
    >
      { `${ length }/${ max }` }
    </small>
  );
};

CharCounter.propTypes = {
  name: PropTypes.string.isRequired,
  max: PropTypes.number.isRequired,
};

// Синхронизирует «можно ли отправлять» в состояние модалки -> disableSubmitButton.
// Отправка доступна только когда выбрана категория и заполнено описание.
const SubmitGuard = (props) => {
  const { onChange } = props;
  const { values } = useFormikContext();
  const subject = values?.subject ?? '';
  const messageBody = values?.messageBody ?? '';
  // disabled, если: нет категории / описание пустое / превышены лимиты (как в Yup)
  const invalid = !values?.categoryRefValueId
    || !messageBody.trim()
    || subject.length > 255
    || messageBody.length > 5000;
  useEffect(() => {
    onChange(invalid);
  }, [invalid, onChange]);
  return null;
};

SubmitGuard.propTypes = {
  onChange: PropTypes.func.isRequired,
};

const FeedbackModal = (props) => {
  const { feedbackStore, onClose } = props;
  const { categories } = feedbackStore;

  const navigate = useNavigate();
  // success-state показываем внутри модалки после успешной отправки
  const [submitted, setSubmitted] = useState(false);
  // submit disabled, пока не выбрана категория и не заполнено описание (см. SubmitGuard)
  const [submitDisabled, setSubmitDisabled] = useState(true);

  useEffect(() => {
    feedbackActions.getCategories().catch(() => {});
  }, []);

  const handleSubmit = async (values) => {
    try {
      // Formik держит isSubmitting до резолва промиса -> submit disabled, без дублей.
      await feedbackActions.create({
        categoryRefValueId: values.categoryRefValueId,
        subject: values.subject || null,
        messageBody: values.messageBody,
        pageUrl: window.location.pathname + window.location.search,
        appVersion: APPConf.version || null,
      });
      setSubmitted(true);
    } catch (errorObj) {
      // при ошибке форму не очищаем (Formik сохраняет значения), просто сообщаем
      toastr.error(APPConf.errorHdr, errorObj?.message || errorObj);
    }
  };

  const goToMy = () => {
    onClose();
    navigate(URL.URL_FEEDBACK_MY);
  };

  if (submitted) {
    return (
      <Modal
        show={ true }
        centered
        backdrop='static'
        aria-labelledby='feedback-success'
        onHide={ onClose }
      >
        <Modal.Header
          style={ { borderBottom: '1px solid #ececec', margin: '0 2px' } }
          closeButton
        >
          <Modal.Title id='feedback-success'>Обращение отправлено</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p style={ { margin: 0 } }>
            Мы получили ваше обращение. Его статус можно отслеживать в разделе «Мои обращения».
          </p>
        </Modal.Body>

        <Modal.Footer style={ { borderTop: '1px solid #ececec', margin: '0 2px' } }>
          <Button variant='outline-primary' text='Закрыть' onClick={ onClose } />
          <Button variant='primary' text='Перейти в Мои обращения' onClick={ goToMy } />
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <FormModal
      header='Создать обращение'
      show={ true }
      onClose={ onClose }
      onCancel={ onClose }
      initialValues={ { categoryRefValueId: null, subject: '', messageBody: '' } }
      onSubmit={ handleSubmit }
      validationSchema={ ConsoleFeedbackValidationSchema }
      submitButtonTitle='Отправить'
      cancelButtonTitle='Отмена'
      showCancelButton
      ignoreDirtyOnSubmit
      disableSubmitButton={ submitDisabled }
    >
      { () => (
        <>
          <Row>
            <Col md={ 6 }>
              <SelectInput
                name='categoryRefValueId'
                label='Категория'
                placeholder='Выберите категорию'
                data={ categories }
                dataTitle='name'
                dataValue='id'
                required
                isSearchable={ false }
              />
            </Col>
          </Row>
          <Row>
            <Col md={ 12 }>
              <StringInput
                name='subject'
                label='Тема'
                placeholder='Коротко опишите проблему или идею'
              />
              <CharCounter name='subject' max={ 255 } />
            </Col>
          </Row>
          <Row>
            <Col md={ 12 }>
              <StringInput
                name='messageBody'
                label='Описание'
                placeholder='Опишите, что произошло, что ожидали увидеть и на каком шаге возникла проблема'
                as='textarea'
                rows={ 5 }
                required
              />
              <CharCounter name='messageBody' max={ 5000 } />
            </Col>
          </Row>
          <Row>
            <Col md={ 12 }>
              <small className='text-muted'>
                Экран консоли и версия приложения будут прикреплены автоматически.
              </small>
            </Col>
          </Row>

          <SubmitGuard onChange={ setSubmitDisabled } />
        </>
      ) }
    </FormModal>
  );
};

FeedbackModal.propTypes = {
  feedbackStore: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default inject('feedbackStore')(observer(FeedbackModal));
